import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { GamesService, BGGSearchResult, BGGGameDetails } from '../generated-api';

interface BGGSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (details: BGGGameDetails) => void;
  initialQuery?: string;
}

const BGGSearchModal: React.FC<BGGSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialQuery = ''
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<BGGSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
      setResults([]);
      setError(null);
    }
  }, [isOpen, initialQuery]);

  // Debounced search with race condition protection
  useEffect(() => {
    if (!isOpen || !query.trim() || query.length < 2) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      // Increment request ID and capture it for this request
      const currentRequestId = ++requestIdRef.current;

      setLoading(true);
      setError(null);

      try {
        const searchResults = await GamesService.searchBggGamesBggSearchGet({
          query: query.trim(),
          limit: 15
        });

        // Only update results if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setResults(searchResults);
        }
      } catch (err) {
        // Only update error if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          console.error('BGG search failed:', err);
          setError('Failed to search BoardGameGeek. Please try again.');
          setResults([]);
        }
      } finally {
        // Only update loading state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query, isOpen]);

  const handleSelect = useCallback(async (result: BGGSearchResult) => {
    setLoadingDetails(result.bggId);
    setError(null);

    try {
      const details = await GamesService.getBggGameDetailsGamesBggBggIdGet({
        bggId: result.bggId
      });
      onSelect(details);
      onClose();
    } catch (err) {
      console.error('Failed to get BGG game details:', err);
      setError('Failed to get game details. Please try again.');
    } finally {
      setLoadingDetails(null);
    }
  }, [onSelect, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-start justify-center p-4 sm:p-6 md:p-8">
        <div className="relative w-full max-w-2xl bg-surface rounded-lg shadow-xl mt-16 sm:mt-24">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-edge">
            <div>
              <h2 className="text-lg font-semibold text-heading">
                Import from BoardGameGeek
              </h2>
              <p className="text-sm text-muted mt-1">
                Search for a game to auto-populate its details
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-disabled hover:text-body p-2"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-4 border-b border-edge">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-disabled" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for a board game..."
                className="w-full pl-10 pr-4 py-2 border border-edge-input rounded-md focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
            </div>
            {loading && (
              <div className="mt-2 text-sm text-muted">
                Searching BoardGameGeek...
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {results.length === 0 && query.length >= 2 && !loading && (
              <div className="p-8 text-center text-muted">
                No games found. Try a different search term.
              </div>
            )}

            {results.length === 0 && query.length < 2 && (
              <div className="p-8 text-center text-muted">
                Enter at least 2 characters to search
              </div>
            )}

            {results.map((result) => (
              <button
                data-track="bgg_result"
                key={result.bggId}
                onClick={() => handleSelect(result)}
                disabled={loadingDetails !== null}
                className="w-full text-left p-4 hover:bg-surface-hover border-b border-edge-subtle last:border-b-0 focus:outline-none focus:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-heading">
                      {result.name}
                    </div>
                    {result.yearPublished && (
                      <div className="text-sm text-muted">
                        Published: {result.yearPublished}
                      </div>
                    )}
                  </div>
                  {loadingDetails === result.bggId ? (
                    <div className="flex items-center text-primary-600">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Loading...
                    </div>
                  ) : (
                    <span className="text-primary-600 text-sm font-medium">
                      Select
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 bg-surface-base rounded-b-lg border-t border-edge">
            <p className="text-xs text-muted text-center">
              Data provided by BoardGameGeek. You can modify the imported details before saving.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BGGSearchModal;
